import { signal, Signal, effect, inject, DestroyRef } from '@angular/core';

export function useSessionState<T>(storageKey: string, defaultState: T) {
  // 1. Recover state from storage if it exists, otherwise use defaults
  const getInitialState = (): T => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : defaultState;
    } catch (e) {
      console.error(`Error parsing session storage for key: ${storageKey}`, e);
      return defaultState;
    }
  };

  // 2. Initialize our reactive signal
  const stateSignal = signal<T>(getInitialState());

  // 3. Automatically sync the signal to sessionStorage whenever it changes
  const destroyRef = inject(DestroyRef);
  const syncEffect = effect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(stateSignal()));
  });

  // Cleanup the effect when the component is destroyed to prevent memory leaks
  destroyRef.onDestroy(() => syncEffect.destroy());

  // 4. Expose updater function
  const updateState = (updater: (currentState: T) => Partial<T>) => {
    stateSignal.update((curr) => ({
      ...curr,
      ...updater(curr),
    }));
  };

  // 5. Expose an explicit reset if a user logs out or leaves the module
  const resetState = () => stateSignal.set(defaultState);

  return {
    state: stateSignal as Signal<T>,
    updateState,
    resetState,
  };
}
